import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, ArrowDown } from "lucide-react";
import { getFunnelReport } from "@/lib/admin.functions";
import { DateRangePicker, defaultRange, type Range } from "@/components/admin/DateRange";
import { SectionTabs, ANALYTICS_TABS } from "@/components/admin/SectionTabs";

export const Route = createFileRoute("/_authenticated/admin/funnels")({
  component: FunnelsPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-20 text-center">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="text-zinc-400">Not found.</div>,
});

const FUNNELS = [
  { id: "studio_to_estimate", label: "Studio → Estimate" },
  { id: "direct_estimate", label: "Direct Estimate" },
  { id: "gallery_to_lead", label: "Gallery → Lead" },
];

function FunnelsPage() {
  const fn = useServerFn(getFunnelReport);
  const [range, setRange] = useState<Range>(defaultRange(30));
  const [funnel, setFunnel] = useState(FUNNELS[0].id);

  const { data, isLoading } = useQuery({
    queryKey: ["funnel", funnel, range],
    queryFn: () => fn({ data: { ...range, funnel } }),
  });

  return (
    <div className="space-y-6">
      <SectionTabs tabs={ANALYTICS_TABS} label="Analytics" />
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase">Conversion</p>
          <h1 className="font-serif text-3xl text-white mt-1">Funnels</h1>
        </div>
        <DateRangePicker value={range} onChange={setRange} />
      </header>

      <div className="flex flex-wrap gap-1.5 bg-white/[0.04] border border-white/10 rounded-full p-1 w-fit">
        {FUNNELS.map((f) => (
          <button key={f.id} onClick={() => setFunnel(f.id)} className={`text-xs px-3 py-1.5 rounded-full ${funnel === f.id ? "bg-amber-300 text-zinc-950 font-semibold" : "text-zinc-400 hover:text-white"}`}>{f.label}</button>
        ))}
      </div>

      {isLoading || !data ? <div className="py-20 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div> : (
        <>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <p className="text-xs text-zinc-500 mb-4">Sessions entered: <span className="text-white font-semibold">{data.totalSessions.toLocaleString()}</span></p>
            <ol className="space-y-2">
              {data.dropOff.map((s: any, i: number) => {
                const max = data.dropOff[0]?.count || 1;
                const width = Math.max(4, (s.count / max) * 100);
                return (
                  <li key={s.step}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-zinc-400">{i + 1}. {s.step.replaceAll("_", " ")}</span>
                      <span className="text-zinc-500">{s.count.toLocaleString()} · {s.conversionFromTop.toFixed(1)}% of top · {s.conversionFromPrev.toFixed(1)}% step-over-step</span>
                    </div>
                    <div className="h-7 bg-white/[0.03] rounded-md overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-amber-300/70 to-amber-200/40 flex items-center justify-end pr-2 text-[10px] text-zinc-900 font-semibold" style={{ width: `${width}%` }}>
                        {s.count}
                      </div>
                    </div>
                    {i < data.dropOff.length - 1 && (
                      <div className="flex justify-center text-zinc-700 py-1"><ArrowDown className="size-3" /></div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>

          <p className="text-xs text-zinc-500">Note: emits funnel events from `src/lib/analytics/track.ts` (e.g. `track.funnelStep("studio_to_estimate", "image_upload", 1)`). Empty steps mean no events were captured yet.</p>
        </>
      )}
    </div>
  );
}